import React, { createContext, useContext, useState, useEffect } from 'react'
import {
  User,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { auth } from '../config/firebase'
import { isDevAdminBypassEnabled } from '../utils/devAdminBypass'

/** 로컬 개발 우회 시 사용할 mock User (email 표시용) */
const createDevBypassUser = (): User =>
  ({ email: 'dev@localhost (로컬 우회)' }) as User

interface AuthContextValue {
  currentUser: User | null
  loading: boolean
  login: (
    email: string,
    password: string,
  ) => Promise<{ success: boolean; user?: User; error?: string }>
  logout: () => Promise<{ success: boolean; error?: string }>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const isDevBypass = isDevAdminBypassEnabled()

  useEffect(() => {
    if (isDevBypass) {
      setCurrentUser(createDevBypassUser())
      setLoading(false)
      return
    }
    if (!auth) {
      setLoading(false)
      return
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user)
      setLoading(false)
    })
    return () => unsubscribe()
  }, [isDevBypass])

  const login = async (email: string, password: string) => {
    if (isDevBypass) {
      setCurrentUser(createDevBypassUser())
      return { success: true, user: createDevBypassUser() }
    }
    if (!auth) {
      return {
        success: false,
        error: 'Firebase가 설정되지 않았습니다. .env를 확인해주세요.',
      }
    }
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      )
      return { success: true, user: userCredential.user }
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string }
      const message =
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/wrong-password'
          ? '이메일 또는 비밀번호가 올바르지 않습니다.'
          : err.message || '로그인에 실패했습니다.'
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    if (isDevBypass) {
      setCurrentUser(null)
      return { success: true }
    }
    if (!auth) return { success: true }
    try {
      await signOut(auth)
      return { success: true }
    } catch (error: unknown) {
      return { success: false, error: (error as Error).message }
    }
  }

  const value = {
    currentUser,
    loading,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
